/** @jsxImportSource @emotion/react */

import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Icon } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import style from './auditDetail.style';
import featureStyle from './feature.style';
import { ProcessItem } from './ProcessItem';
import {
  ACTION_PLAN_STATUS,
  ActionPlanSubmitRequest,
  ActionPlanUpdateRequest,
  areAllTopicsValid,
  Audit,
  FollowUpStatus,
  FollowUpUpdateRequest,
  getActionPlanSubProcessError,
  getSentToTheStoreProcessError,
  hasActionPlanCoordinatorNote,
  hasActionPlanCoordinatorNoteByProcess,
  hasAuditorFollowUpNote,
  hasAuditorFollowUpNoteByProcess,
  hasAuditorNote,
  hasAuditorNoteByProcess,
  hasManagerNote,
  hasManagerNoteByProcess,
  hasRegionalManagerNoteByProcess,
  isProcessAttachmentsToBeChecked,
  isProcessPassed,
  Priority,
  Process,
  ResultStatus,
  ResultUpdateRequest,
  SENT_TO_THE_STORES_PROCESS_STATUS,
  SubProcess,
} from '../model/assessmentDetail.model';
import { Status, STATUS } from '../model/assessmentStatus';
import { ModalPage } from '../../../shared/ModalPage';
import { CheckList } from './CheckList';
import {
  saveActionPlan,
  saveFollowUp,
  saveSubProcessResult,
  selectAssessment,
  selectIsActionPlanEditor,
  selectIsBrandCoordinator,
  selectIsInDistributionList,
  selectIsReadOnlyStoreUser,
  selectTopics,
  submitActionPlan,
} from '../store/assessmentDetailSlice';
import { CheckListAndActionPlanContainer } from './CheckListAndActionPlanContainer';
import { toastService } from '../../../core/services/toastService';
import { selectPrincipal } from '../../auth/store/principalSlice';
import { checkRole, ROLES } from '../../auth/model/principal.model';
import { useAppDispatch } from '../../../core/store';
import { AuditMultiToggle } from './AuditMultiToggle';
import { TopicContainer } from './TopicContainer';
import { setUnsavedChangesPresence } from '../../../core/unsavedChangesSlice';
import { isValidFollowUpStatus } from '../model/assessmentFollowUp.model';

interface SentToTheStoresAuditDetailProps {
  audit: Audit;
  assessmentStatus: Status;
  followUpStatus: FollowUpStatus;
}

export const SentToTheStoresAuditDetail = ({
  audit,
  assessmentStatus,
  followUpStatus,
}: SentToTheStoresAuditDetailProps): JSX.Element => {
  const { t } = useTranslation();
  const findingPresent = useCallback(() => audit.processes.flatMap(p => p.subProcesses).length > 0, [audit.processes]);
  const isReadOnlyStoreUser = useSelector(selectIsReadOnlyStoreUser);
  const isActionPlanCoordinator = useSelector(selectIsBrandCoordinator);
  const isActionPlanEditor = useSelector(selectIsActionPlanEditor);
  const principal = useSelector(selectPrincipal);
  const isAuditor = useMemo(() => checkRole(principal, ROLES.AUDITOR), [principal]);
  const isAuditorManager = useMemo(() => checkRole(principal, ROLES.AUDITOR_MANAGER), [principal]);
  const isAuditAdmin = useMemo(() => checkRole(principal, ROLES.AUDIT_ADMIN), [principal]);
  const isInDistributionList = useSelector(selectIsInDistributionList);
  const isAPCoordinator = useSelector(selectIsBrandCoordinator);

  const [isTopicContainerActive, setIsTopicContainerActive] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [onlyFindingsFilter, setOnlyFindingsFilter] = useState(false);
  const [onlyFollowUpFilter, setOnlyFollowUpFilter] = useState(false);
  const [onlyFollowUpToFillInFilter, setOnlyFollowUpToFillInFilter] = useState(false);
  const [onlyPendingActionPlanFilter, setOnlyPendingActionPlanFilter] = useState(false);
  const isFollowUpToFillInToggleVisible = followUpStatus === FollowUpStatus.UNDER_FOLLOWUP;
  const [priorityFilter, setPriorityFilter] = useState<Priority[]>([]);
  const topics = useSelector(selectTopics);
  const assessment = useSelector(selectAssessment);

  const isFollowUpToggleVisible =
    (followUpStatus === FollowUpStatus.UNDER_FOLLOWUP &&
      (isAuditAdmin || isActionPlanCoordinator || isActionPlanEditor || isInDistributionList)) ||
    ([FollowUpStatus.DONE, FollowUpStatus.PENDING, FollowUpStatus.COMPLETED].includes(followUpStatus) &&
      (isAuditor || isAuditorManager || isAuditAdmin || isActionPlanCoordinator || isActionPlanEditor));

  const isFindingsToggleVisible = isAuditor || isAuditorManager || isAuditAdmin || isInDistributionList;

  const isToggleFieldVisible = isFindingsToggleVisible || isFollowUpToggleVisible;

  const filterByFollowUp = useCallback((subProcess: SubProcess): boolean => {
    return subProcess.followUp != null && subProcess.followUp.result != null;
  }, []);

  const canEditFollowUp = useCallback(
    (subProcess: SubProcess): boolean => {
      const isAPEditor = principal.email != null && subProcess.actionPlan?.editor?.email === principal.email;
      return (
        subProcess.followUp != null &&
        isValidFollowUpStatus(followUpStatus) &&
        subProcess.actionPlan?.status === ACTION_PLAN_STATUS.SCHEDULED &&
        followUpStatus === FollowUpStatus.UNDER_FOLLOWUP &&
        (isAPCoordinator || isAPEditor || isAuditAdmin || isInDistributionList) &&
        !subProcess.followUp?.completed
      );
    },
    [followUpStatus, isAPCoordinator, isAuditAdmin, isInDistributionList, principal.email]
  );

  const filterByFollowUpToFillIn = useCallback(
    (subProcess: SubProcess): boolean => {
      return canEditFollowUp(subProcess);
    },
    [canEditFollowUp]
  );

  const getFilteredProcesses = useCallback(() => {
    if (audit == null) {
      return [];
    }
    return audit.processes.filter(p =>
      p.subProcesses.some(
        sp =>
          (!onlyFindingsFilter || sp.result.status === ResultStatus.FAILED) &&
          (!onlyFollowUpFilter || filterByFollowUp(sp)) &&
          (!onlyFollowUpToFillInFilter || filterByFollowUpToFillIn(sp)) &&
          (!onlyPendingActionPlanFilter || getActionPlanSubProcessError(sp) != null) &&
          (priorityFilter.length === 0 || priorityFilter.some(pf => pf === sp.result.priority || pf == null))
      )
    );
  }, [
    audit,
    onlyFindingsFilter,
    onlyFollowUpFilter,
    onlyFollowUpToFillInFilter,
    filterByFollowUp,
    filterByFollowUpToFillIn,
    priorityFilter,
    onlyPendingActionPlanFilter,
  ]);

  const getFilteredSubProcess = useCallback(
    (p: Process) =>
      p.subProcesses.filter(
        sp =>
          (!onlyFindingsFilter || sp.result.status === ResultStatus.FAILED) &&
          (!onlyFollowUpFilter || filterByFollowUp(sp)) &&
          (!onlyFollowUpToFillInFilter || filterByFollowUpToFillIn(sp)) &&
          (!onlyPendingActionPlanFilter || getActionPlanSubProcessError(sp) != null) &&
          (priorityFilter.length === 0 || priorityFilter.some(pf => pf === sp.result.priority || pf == null))
      ),
    [
      onlyFindingsFilter,
      onlyFollowUpFilter,
      onlyFollowUpToFillInFilter,
      filterByFollowUp,
      filterByFollowUpToFillIn,
      priorityFilter,
      onlyPendingActionPlanFilter,
    ]
  );

  const onChangeTopicActive = () => {
    setIsTopicContainerActive(!isTopicContainerActive);
    setActiveIndex(-1);
  };

  const onChangeProcessActive = (index: number) => {
    setIsTopicContainerActive(false);
    setActiveIndex(index);
  };

  return (
    <>
      {isToggleFieldVisible && (
        <div css={style.findingsFilter}>
          <AuditMultiToggle
            editable
            defaultFindingsValue={onlyFindingsFilter}
            defaultFollowUpValue={onlyFollowUpFilter}
            defaultFollowUpToFillInValue={onlyFollowUpToFillInFilter}
            defaultPendingActionPlanValue={onlyPendingActionPlanFilter}
            defaultPriorityFilter={priorityFilter}
            showFindings={isFindingsToggleVisible}
            showFollowUp={isFollowUpToggleVisible}
            showFollowUpToFillIn={isFollowUpToFillInToggleVisible}
            showPendingActionPlan
            onFindingsChange={value => {
              setOnlyFindingsFilter(value);
            }}
            onFollowUpChange={value => {
              setOnlyFollowUpFilter(value);
            }}
            onFollowUpToFillInChange={value => {
              setOnlyFollowUpToFillInFilter(value);
            }}
            onPendingActionPlanChange={value => {
              setOnlyPendingActionPlanFilter(value);
            }}
            onPriorityFilterChange={value => {
              setPriorityFilter(value);
            }}
          />
        </div>
      )}
      <div css={style.body}>
        {findingPresent() || !areAllTopicsValid(assessment) ? (
          <>
            {getFilteredProcesses().length === 0 && <div css={style.noItems}>{t('assessment.audit.noFindings')}</div>}
            {getFilteredProcesses().map((p, index) => {
              return (
                p.subProcesses.length > 0 && (
                  <ProcessItem
                    process={p}
                    index={index}
                    key={p.code}
                    activeIndex={activeIndex}
                    setActiveIndex={onChangeProcessActive}
                    icon={
                      <>
                        {hasManagerNoteByProcess(p) && <Icon name='sticky note' css={style.noteIcon} />}
                        {hasAuditorNoteByProcess(p) && <Icon name='sticky note' css={style.auditorNoteIcon} />}
                        {hasAuditorFollowUpNoteByProcess(p) && (
                          <Icon name='sticky note' css={style.auditorFollowUpNoteIcon} />
                        )}
                        {hasRegionalManagerNoteByProcess(p) && (
                          <Icon name='sticky note' css={style.regionalManagerNoteIcon} />
                        )}
                        {hasActionPlanCoordinatorNoteByProcess(p) && (
                          <Icon name='sticky note' css={style.actionPlanCoordinatorNoteIcon} />
                        )}
                        {(isAuditor || isAuditorManager || isAuditAdmin) && isProcessAttachmentsToBeChecked(p) && (
                          <Icon className='fas fa-unlink' css={style.notMandatoryAttachmentsIcon} />
                        )}
                        {isReadOnlyStoreUser &&
                          (getSentToTheStoreProcessError(p) === SENT_TO_THE_STORES_PROCESS_STATUS.SUCCESS ? (
                            <Icon name='check circle' css={style.successIcon} />
                          ) : (
                            <Icon name='exclamation circle' css={style.errorIcon} />
                          ))}
                        {(isAuditor || isAuditorManager || isAuditAdmin || isAuditAdmin) &&
                          (isProcessPassed(p) ? (
                            <Icon name='clipboard check' css={style.successIcon} />
                          ) : (
                            <Icon name='clipboard list' css={style.errorIcon} />
                          ))}
                      </>
                    }>
                    {getFilteredSubProcess(p).map(sp => (
                      <SubProcessContainer
                        key={sp.code}
                        subProcess={sp}
                        assessmentStatus={assessmentStatus}
                        followUpStatus={followUpStatus}
                      />
                    ))}
                  </ProcessItem>
                )
              );
            })}

            {topics.length !== 0 && (
              <TopicContainer
                onChangeTopicActive={onChangeTopicActive}
                isTopicContainerActive={isTopicContainerActive}
              />
            )}
          </>
        ) : (
          <h4 style={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
            {t('assessment.audit.noFindingsUserStoreMessage')} {'\u{1F680}'}
          </h4>
        )}
      </div>
    </>
  );
};

interface SubProcessItemProps {
  subProcess: SubProcess;
  assessmentStatus: Status;
  followUpStatus: FollowUpStatus;
}

const SubProcessContainer = ({ subProcess, assessmentStatus, followUpStatus }: SubProcessItemProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const getError = useCallback(() => getActionPlanSubProcessError(subProcess), [subProcess]);
  const [isCheckListOpen, setCheckListOpen] = useState(false);
  const isReadOnlyStoreUser = useSelector(selectIsReadOnlyStoreUser);
  const principal = useSelector(selectPrincipal);
  const isAPEditor = principal.email != null && subProcess.actionPlan?.editor?.email === principal.email;
  const isAuditor = checkRole(principal, ROLES.AUDITOR);
  const isAuditorManager = checkRole(principal, ROLES.AUDITOR_MANAGER);
  const isAuditAdmin = checkRole(principal, ROLES.AUDIT_ADMIN);

  const canEditCheckList = useMemo(
    () => assessmentStatus.code !== STATUS.CLOSED.code && isAuditAdmin,
    [assessmentStatus.code, isAuditAdmin]
  );

  const handleOnSaveActionPlan = useCallback(
    async (request: ActionPlanUpdateRequest) => {
      const response = await dispatch(saveActionPlan(request));
      if (response != null) {
        toastService.success();
      } else {
        setCheckListOpen(true);
      }
    },
    [dispatch]
  );

  const handleOnSaveFollowUp = useCallback(
    async (request: FollowUpUpdateRequest) => {
      const response = await dispatch(saveFollowUp(request));
      if (response != null) {
        await dispatch(setUnsavedChangesPresence(false));
        toastService.success();
      } else setCheckListOpen(true);
    },
    [dispatch]
  );

  const handleOnSubmitActionPlan = useCallback(
    async (updateRequest: ActionPlanUpdateRequest, submitRequest: ActionPlanSubmitRequest) => {
      const response =
        (await dispatch(saveActionPlan(updateRequest))) && (await dispatch(submitActionPlan(submitRequest)));
      if (response != null) {
        toastService.success();
        setCheckListOpen(false);
      }
    },
    [dispatch]
  );

  const handleOnSaveChecklist = useCallback(
    async (request: ResultUpdateRequest) => {
      const response = await dispatch(saveSubProcessResult(request));
      if (response != null) {
        toastService.success();
      }
      setCheckListOpen(false);
    },
    [dispatch]
  );

  return (
    <>
      {isCheckListOpen && (
        <ModalPage onClose={() => setCheckListOpen(false)} title={`${subProcess.code} ${subProcess.description}`}>
          {(isReadOnlyStoreUser || isAuditAdmin || isAPEditor) && subProcess.result.status !== ResultStatus.PASSED ? ( // TODO read only permission
            <CheckListAndActionPlanContainer
              subProcess={subProcess}
              assessmentStatus={STATUS.SENT_TO_THE_STORES}
              onActionPlanSave={handleOnSaveActionPlan}
              onActionPlanSubmit={handleOnSubmitActionPlan}
              onFollowUpSave={handleOnSaveFollowUp}
              onChecklistSave={handleOnSaveChecklist}
              followUpStatus={followUpStatus}
            />
          ) : (
            <CheckList
              result={subProcess.result}
              subProcessInfo={subProcess.info}
              assessmentStatus={STATUS.SENT_TO_THE_STORES}
              processCode={subProcess.processCode}
              subProcessCode={subProcess.code}
              subProcessDescription={subProcess.description}
              readonly={!canEditCheckList}
              isVisibleResultBox={isAuditor || isAuditorManager || isAuditAdmin}
              onSave={canEditCheckList ? handleOnSaveChecklist : undefined}
            />
          )}
        </ModalPage>
      )}
      <div css={style.subProcess} onClick={() => setCheckListOpen(true)}>
        {((isReadOnlyStoreUser || isAPEditor) && subProcess.result.status !== ResultStatus.PASSED) ||
        canEditCheckList ? (
          <Icon name='pencil' css={style.subProcessActionIcon} />
        ) : (
          <Icon name='search' css={style.subProcessActionIcon} />
        )}
        <div css={style.subProcessDescription}>
          {subProcess.code} - {subProcess.description}
        </div>
        {hasManagerNote(subProcess) && <Icon name='sticky note' css={style.noteIcon} />}
        {hasAuditorNote(subProcess) && <Icon name='sticky note' css={style.auditorNoteIcon} />}
        {hasAuditorFollowUpNote(subProcess) && <Icon name='sticky note' css={style.auditorFollowUpNoteIcon} />}
        {hasActionPlanCoordinatorNote(subProcess) && (
          <Icon name='sticky note' css={style.actionPlanCoordinatorNoteIcon} />
        )}
        {isAuditor && subProcess.actionPlan != null && subProcess.actionPlan.attachmentsToBeChecked && (
          <Icon className='fas fa-unlink' css={style.notMandatoryAttachmentsIcon} />
        )}
        {subProcess.result.status === ResultStatus.FAILED ? (
          <Icon className='fas fa-circle' css={featureStyle.priorityIconColor(subProcess.result.priority)} />
        ) : (
          <Icon name='clipboard check' css={style.successIcon} />
        )}
        {(isReadOnlyStoreUser || isAPEditor) && // TODO read only permission
          (getError() != null ? (
            <Icon name='exclamation circle' css={style.errorIcon} />
          ) : (
            <Icon name='check circle' css={style.successIcon} />
          ))}
      </div>
    </>
  );
};
